import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import MDButton from "components/MDButton";
import pvmCodes from "common/VATClassifications/pvmCodes";
import xmlTypeLabel from "common/utils/xmlTypeLabel";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";
import api from "../../services/api";

const emptyFields = { warehouseCode: "", groupName: "", pvmCode: "", xmlType: "" };
const fromInfo = (info) => ({
  warehouseCode: info.warehouse_code || "",
  groupName: info.group_name || "",
  pvmCode: info.pvm_code || "",
  xmlType: info.xml_type || "",
});

function SupplierFields({ value, onChange, t }) {
  return (
    <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
      <TextField
        label={t("warehouseCode")}
        value={value.warehouseCode}
        onChange={(event) => onChange({ ...value, warehouseCode: event.target.value })}
        size="small"
        required
        fullWidth
      />
      <TextField
        label={t("groupName")}
        value={value.groupName}
        onChange={(event) => onChange({ ...value, groupName: event.target.value })}
        size="small"
        required
        fullWidth
      />
      <Autocomplete
        options={pvmCodes}
        getOptionLabel={(option) => option.label}
        value={pvmCodes.find((item) => item.value === value.pvmCode) || null}
        onChange={(_, option) => onChange({ ...value, pvmCode: option?.value || "" })}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        renderInput={(params) => (
          <TextField {...params} label={t("pvmCode")} size="small" required />
        )}
      />
      <TextField
        label={t("xmlType")}
        select
        value={value.xmlType}
        onChange={(event) => onChange({ ...value, xmlType: event.target.value })}
        size="small"
        required
        fullWidth
      >
        <MenuItem value="quantitative">{t("quantitative")}</MenuItem>
        <MenuItem value="summary">{t("summary")}</MenuItem>
      </TextField>
    </Box>
  );
}

SupplierFields.propTypes = {
  value: PropTypes.shape({
    warehouseCode: PropTypes.string,
    groupName: PropTypes.string,
    pvmCode: PropTypes.string,
    xmlType: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function SupplierEdit({ open, onClose, supplier, onUpdate }) {
  const { selectedCompany } = useCompany();
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [infos, setInfos] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [newFields, setNewFields] = useState(emptyFields);
  const [editingId, setEditingId] = useState(null);
  const [editingFields, setEditingFields] = useState(emptyFields);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const companyId = selectedCompany?.id;
  const registration = supplier.supplier_registration;

  const refreshInfos = async () => {
    const response = await api.get("/api/suppliers/info/", {
      params: { supplier_registration: registration, company_id: companyId },
    });
    if (!response.data.success) throw new Error(response.data.error);
    setInfos([...response.data.infos].sort((a, b) => Number(b.is_primary) - Number(a.is_primary)));
  };

  useEffect(() => {
    if (!open || !companyId) return undefined;
    let active = true;
    setNewFields(emptyFields);
    setEditingId(null);
    setDeleteId(null);
    setError("");
    setLoading(true);
    Promise.all([
      api.get("/api/suppliers/info/", {
        params: { supplier_registration: registration, company_id: companyId },
      }),
      api.get("/api/suppliers/options/", { params: { company_id: companyId } }),
    ])
      .then(([infoResponse, optionResponse]) => {
        if (!active) return;
        if (!infoResponse.data.success) throw new Error(infoResponse.data.error);
        setInfos(
          [...infoResponse.data.infos].sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
        );
        setCombinations(optionResponse.data.combinations || []);
      })
      .catch((requestError) => {
        if (active) setError(requestError.response?.data?.error || requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, companyId, registration]);

  const save = async (fields, positionId = null) => {
    if (Object.values(fields).some((value) => !String(value || "").trim())) {
      setError(t("supplierFieldsRequired"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post(
        "/api/update_supplier_info/",
        {
          supplierRegistration: registration,
          ...fields,
          ...(positionId ? { positionId } : {}),
        },
        { params: { company_id: companyId } }
      );
      await refreshInfos();
      setNewFields(emptyFields);
      setEditingId(null);
      onUpdate();
      showSnackbar(t("savedSuccessfully"), "success");
    } catch (requestError) {
      setError(requestError.response?.data?.error || t("errorOccurred"));
    } finally {
      setBusy(false);
    }
  };

  const makePrimary = async (infoId) => {
    setBusy(true);
    setError("");
    try {
      await api.post(
        "/api/set-selected-position/",
        {
          positionId: infoId,
          supplierRegistration: registration,
        },
        { params: { company_id: companyId } }
      );
      await refreshInfos();
      onUpdate();
      showSnackbar(t("updatedSuccessfully"), "success");
    } catch (requestError) {
      setError(requestError.response?.data?.error || t("errorOccurred"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(
        "/api/remove-position/",
        { positionId: deleteId },
        {
          params: { company_id: companyId },
        }
      );
      setDeleteId(null);
      await refreshInfos();
      onUpdate();
      showSnackbar(t("deletedSuccessfully"), "success");
    } catch (requestError) {
      setError(requestError.response?.data?.error || t("errorOccurred"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t("supplierSettingsTitle")}: {supplier.name}
          <Typography variant="body2" color="text.secondary">
            {registration}
          </Typography>
          <IconButton
            aria-label={t("close")}
            onClick={onClose}
            disabled={busy}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {loading && (
            <Box py={3} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!loading && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" mb={1}>
                  {t("savedVariants")}
                </Typography>
                {infos.length === 0 && (
                  <Alert severity="warning">{t("supplierHasNoSettings")}</Alert>
                )}
                <Stack spacing={0.75}>
                  {infos.map((info) => (
                    <Paper key={info.id} variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                      {editingId === info.id ? (
                        <Stack spacing={2}>
                          <SupplierFields value={editingFields} onChange={setEditingFields} t={t} />
                          <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
                            <MDButton
                              variant="outlined"
                              color="secondary"
                              disabled={busy}
                              onClick={() => setEditingId(null)}
                            >
                              {t("cancel")}
                            </MDButton>
                            <MDButton
                              variant="contained"
                              color="info"
                              disabled={busy}
                              onClick={() => save(editingFields, info.id)}
                            >
                              {t("save")}
                            </MDButton>
                          </Box>
                        </Stack>
                      ) : (
                        <Box
                          display="flex"
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={1.5}
                        >
                          <Box minWidth={0} flex="1 1 280px">
                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                              <Typography variant="body2" fontWeight="medium">
                                {info.warehouse_code} / {info.group_name}
                              </Typography>
                              {info.is_primary && (
                                <Chip
                                  size="small"
                                  color="success"
                                  label={t("primary")}
                                  sx={{ height: 22 }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t("pvmCode")}: {info.pvm_code} · {t("xmlType")}:{" "}
                              {xmlTypeLabel(info.xml_type, t)}
                            </Typography>
                          </Box>
                          <Box display="flex" gap={0.75} flexWrap="wrap" flex="0 0 auto">
                            <MDButton
                              variant="outlined"
                              color="info"
                              size="small"
                              sx={{ minWidth: "auto", px: 1.25, py: 0.5 }}
                              disabled={busy}
                              onClick={() => {
                                setEditingId(info.id);
                                setEditingFields(fromInfo(info));
                              }}
                            >
                              {t("edit")}
                            </MDButton>
                            {!info.is_primary && (
                              <MDButton
                                variant="outlined"
                                color="success"
                                size="small"
                                sx={{ minWidth: "auto", px: 1.25, py: 0.5 }}
                                disabled={busy}
                                onClick={() => makePrimary(info.id)}
                              >
                                {t("setPrimary")}
                              </MDButton>
                            )}
                            <MDButton
                              variant="outlined"
                              color="error"
                              size="small"
                              sx={{ minWidth: "auto", px: 1.25, py: 0.5 }}
                              disabled={busy}
                              onClick={() => setDeleteId(info.id)}
                            >
                              {t("delete")}
                            </MDButton>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="h6" mb={1}>
                  {t("addSupplierVariant")}
                </Typography>
                {combinations.length > 0 && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("copyExistingSettings")}
                    value=""
                    sx={{ mb: 2 }}
                    onChange={(event) =>
                      setNewFields(fromInfo(combinations[Number(event.target.value)]))
                    }
                  >
                    {combinations.map((combo, index) => (
                      <MenuItem
                        key={`${combo.warehouse_code}-${combo.group_name}-${index}`}
                        value={index}
                      >
                        {combo.warehouse_code} / {combo.group_name} / {combo.pvm_code} /{" "}
                        {xmlTypeLabel(combo.xml_type, t)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <SupplierFields value={newFields} onChange={setNewFields} t={t} />
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <MDButton
                    variant="contained"
                    color="info"
                    disabled={busy}
                    onClick={() => save(newFields)}
                  >
                    {t("addSupplierVariant")}
                  </MDButton>
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteId !== null}
        onClose={busy ? undefined : () => setDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("deleteSupplierVariant")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("confirmDeleteSupplierVariant")}</DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            disabled={busy}
            onClick={() => setDeleteId(null)}
          >
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="error" disabled={busy} onClick={remove}>
            {t("delete")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

SupplierEdit.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  supplier: PropTypes.shape({
    name: PropTypes.string,
    supplier_registration: PropTypes.string.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default SupplierEdit;
